/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactNode, useEffect, useRef } from "react";
import { useCopyToClipboard } from "react-use";
import { noop } from "~/tools";
import { ButtonWrap } from "../Styled";

type Props = {
  text: string;
  children: ReactNode;
  onCopy?: () => void;
  onError?: (message: string) => void;
};

export function CopyToClipboard({ text, children, onCopy = noop, onError = noop }: Props) {
  const [state, copyToClipboard] = useCopyToClipboard();
  const userInteraction = useRef(false);

  // Workaround for unstable state of useCopyToClipboard
  // Sometimes it returns old value, and it causes call of onCopy function
  // @see https://github.com/streamich/react-use/issues/1265
  const handleClick = () => {
    copyToClipboard(text);
    userInteraction.current = true;
  };

  useEffect(() => {
    if (state.error != null) {
      onError(state.error.message);

      return;
    }

    if (state.value != null && userInteraction.current) {
      onCopy();
      userInteraction.current = false;
    }
  }, [state, onError, onCopy]);

  return (
    <ButtonWrap css={{ cursor: "pointer" }} onClick={handleClick}>
      {children}
    </ButtonWrap>
  );
}
